import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import Flex from 'antd/es/flex';
import Modal from 'antd/es/modal/Modal';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import useResizeLayout from '@Hooks/useResizeLayout';
import ViewContentButton from '@Features/MaterialRequest/requests/components/ComparisonSheet/common/ViewContentButton/ViewContentButton';
import { ViewWidget } from '@Components/ViewWidget';
import { Formik } from 'formik';
import { usePrepareComparisonSheetData } from '@Features/MaterialRequest/requests/components/ComparisonSheet/hooks/useGetComparisonSheetData';
import { comparisonSheetOffersSelector } from '@Features/MaterialRequest/requests/MaterialRequisition/store/selectors/materialRequestSelector';
import { useAppSelector } from '@Hooks/index';
import {
  ComparisonSheetSelectedItem,
  ComparisonSheetState,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';
import { TenderUpdateRequest } from '@Types/api/form/tenderRequest/TenderUpdateRequest';
import {
  getDisabledItemsByAccess,
  getSplitMaterialIDs,
  isComparisonSheetRecordChecked,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/utils';
import { SheetLayout } from '../components/SheetLayout';
import styles from './ComparisonSheetScreen.module.css';
import RenderHeader from '../components/SheetLayout/components/RenderHeader/RenderHeader';
import TabSwitch from './TabSwitch/TabSwitch';
import { ComparisonTableTabs } from './Tab.enum';
import {
  useGetTenderAccessQuery,
  useGetTenderQuery,
  useUpdateTenderMutation,
} from '../../store/api/TenderRequestApiSlice';

import TenderInfo from './components/TenderInfo/TenderInfo';
import MaterialInfo from './components/MaterialInfo/MaterialInfo';
import { MaterialVendorItem } from './TabSwitch/tabs/MaterialsTab/types';
import {
  CM_OPTION,
  IS_COMPLY,
  PRIORITY_ID,
  REF_CODE,
  REMARKS,
} from './utils/const';
import {
  // PrintComparisonBtn,
  TenderInfoBtn,
} from './buttons';

export const ComparisonSheetScreen: FC<unknown> = () => {
  const navigate = useNavigate();
  const onCancel = useCallback(() => {
    navigate('../');
  }, [navigate]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [tab, setTab] = useState<ComparisonTableTabs | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSetting, setModalSetting] = useState({
    title: 'Tender',
    caption: '',
  });

  const allOffers = useAppSelector(comparisonSheetOffersSelector);

  const { isMobile } = useResizeLayout();
  const { tenderId } = useParams();

  const { data: tenderDetails } = useGetTenderQuery(tenderId || '', {
    skip: !tenderId,
  });

  const [updateTender] = useUpdateTenderMutation();
  const { data: access } = useGetTenderAccessQuery(tenderId || '', {
    skip: !tenderId,
  });

  const tenderDetailsNumber = useMemo(() => {
    if (tenderDetails) {
      return tenderDetails.id;
    }
    return '';
  }, [tenderDetails]);

  useEffect(() => {
    const lastPath = location.pathname.split(/\//).reverse()[0];
    setTab(
      lastPath === MRFeatureRoutePath.COMPARISON_SHEET_SELECTED
        ? ComparisonTableTabs.Selected
        : ComparisonTableTabs.Materials
    );
  }, [location]);

  const changeTab = useCallback(
    (tabName: ComparisonTableTabs) => {
      if (tabName === ComparisonTableTabs.Materials) {
        navigate('');
      } else {
        navigate(MRFeatureRoutePath.COMPARISON_SHEET_SELECTED);
      }
    },
    [navigate]
  );

  const isApproveTab = location.pathname.includes('approve');

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (searchParams.get('materialView')) {
      setModalSetting({
        title: 'Material',
        caption: `Material No.${(location?.state as MaterialVendorItem).material?.code}`,
      });
    } else {
      setModalSetting({
        title: 'Tender',
        caption: `${modalSetting.title} ${tenderDetailsNumber}`,
      });
    }
  }, [location?.state, modalSetting.title, searchParams, tenderDetailsNumber]);

  const { loading } = usePrepareComparisonSheetData();

  const handleSave = useCallback(
    async (values: ComparisonSheetState) => {
      if (tenderId) {
        const newReplyItemsState = values.allOffers
          ?.map((offer) => {
            const records = offer.vendorOffers.map((vOffer) => ({
              matId: offer.material.matId,
              offer: {
                ...vOffer,
                itemRec: isComparisonSheetRecordChecked(
                  values.selectedOffersIDs,
                  offer.matId,
                  vOffer.vendorId
                ),
              },
            }));
            return records;
          })
          .flat();
        if (newReplyItemsState) {
          const updateTenderBody: TenderUpdateRequest = {
            priorityId: PRIORITY_ID,
            cmOption: CM_OPTION,
            tenderDataList: newReplyItemsState.map(
              ({
                matId,
                offer: { vendorId, tenderItemId, rate, freeItem, itemRec },
              }) => ({
                reqItemId: Number(tenderItemId),
                matId: matId!,
                refCode: REF_CODE,
                vendorId: Number(vendorId),
                unitRate: rate,
                itemRec,
                freeItem: freeItem!,
                remarks: REMARKS,
                isComply: IS_COMPLY,
              })
            ),
          };

          await updateTender({ tenderId, body: updateTenderBody });
        }
      }
    },
    [tenderId, updateTender]
  );

  const initialState: ComparisonSheetState = useMemo(() => {
    if (!loading) {
      const selectedOffersIDs = allOffers?.reduce((selected, curOffer) => {
        const alreadySelected = curOffer.vendorOffers
          .filter((vOffer) => vOffer.itemRec)
          .map((vOffer) => {
            return { vendorId: vOffer.vendorId };
          });
        if (alreadySelected.length) {
          const withMatId = alreadySelected.map((selectedData) => ({
            ...selectedData,
            matId: curOffer.matId,
          }));
          selected.push(...withMatId);
        }
        return selected;
      }, [] as ComparisonSheetSelectedItem[]);
      const splitMaterials = getSplitMaterialIDs(selectedOffersIDs);

      const disabledItems = getDisabledItemsByAccess(access);

      return {
        selectedOffersIDs,
        allOffers,
        splitMaterials,
        disabledItems,
      };
    }
    return { selectedOffers: undefined, allOffers, splitMaterials: [] };
  }, [access, allOffers, loading]);

  return (
    <Modal
      open
      className={styles.modal}
      width="100%"
      height="100%"
      closable={false}
      footer={null}
    >
      <SheetLayout
        header={
          <RenderHeader
            headerCaption="Price Comparison"
            onCancel={onCancel}
            handleBack={onCancel}
            HeaderButtons={
              <>
                <TenderInfoBtn />
                {/* <PrintComparisonBtn /> */}
              </>
            }
          />
        }
      >
        <>
          {isMobile && (
            <ViewContentButton
              caption={modalSetting.caption}
              handleView={handleOpenModal}
            />
          )}
          <ViewWidget
            title={`${modalSetting.title} Details`}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          >
            {modalSetting.title === 'Tender' && (
              <TenderInfo tenderId={String(tenderDetailsNumber)} />
            )}
            {modalSetting.title === 'Material' && (
              <MaterialInfo
                material={(location?.state as MaterialVendorItem)?.material}
              />
            )}
          </ViewWidget>
          {!isApproveTab && <TabSwitch changeTab={changeTab} tab={tab} />}
          <Flex
            flex="1 0 100%"
            className={classNames(styles['outlet-container'])}
            vertical
          >
            <Formik
              initialValues={initialState}
              onSubmit={handleSave}
              enableReinitialize
            >
              <Flex vertical>{!loading && <Outlet />}</Flex>
            </Formik>
          </Flex>
        </>
      </SheetLayout>
    </Modal>
  );
};
